"use client"

import React, { useState, useEffect } from "react"
import {
  Search,
  Download,
  FileText,
  Users,
  Award,
  ChevronDown,
  Filter,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Info,
  TrendingUp,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  HelpCircle, // Icon for quiz
  Star, // Icon for score
  Check, // Icon for passed
} from "lucide-react"
import * as XLSX from "xlsx"
import axios from "axios"
import jsPDF from "jspdf";
import "jspdf-autotable";
// Cache and debounce utilities
const COURSE_PROGRESS_CACHE = new Map()
const API_DEBOUNCE_DELAY = 500 // ms

// Debounce function
const debounce = (func, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(null, args), delay)
  }
}

// Attractive Loader Component
const AttractiveLoader = () => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        padding: "20px",
      }}
    >
      {/* Main loader animation */}
      <div
        style={{
          position: "relative",
          width: "80px",
          height: "80px",
          marginBottom: "30px",
        }}
      >
        {/* Outer ring */}
        <div
          style={{
            position: "absolute",
            width: "80px",
            height: "80px",
            border: "4px solid #e2e8f0",
            borderTop: "4px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        ></div>
        {/* Inner ring */}
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            width: "60px",
            height: "60px",
            border: "3px solid #e2e8f0",
            borderTop: "3px solid #8b5cf6",
            borderRadius: "50%",
            animation: "spin 1.5s linear infinite reverse",
          }}
        ></div>
        {/* Center dot */}
        <div
          style={{
            position: "absolute",
            top: "35px",
            left: "35px",
            width: "10px",
            height: "10px",
            backgroundColor: "#3b82f6",
            borderRadius: "50%",
            animation: "pulse 2s ease-in-out infinite",
          }}
        ></div>
      </div>

      {/* Loading text with gradient */}
      <div
        style={{
          background: "linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899)",
          backgroundSize: "200% 200%",
          animation: "gradient 3s ease infinite",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          fontSize: "24px",
          fontWeight: "bold",
          marginBottom: "10px",
          textAlign: "center",
        }}
      >
        Loading Dashboard
      </div>

      {/* Subtitle */}
      <p
        style={{
          color: "#64748b",
          fontSize: "16px",
          textAlign: "center",
          marginBottom: "30px",
        }}
      >
        Fetching your data and preparing analytics...
      </p>

      {/* Progress dots */}
      <div style={{ display: "flex", gap: "8px" }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#3b82f6",
              borderRadius: "50%",
              animation: `bounce 1.4s ease-in-out infinite both`,
              animationDelay: `${i * 0.16}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Add CSS animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.7; }
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>
    </div>
  )
}

// Optimized Course Modal Component
const CourseModal = ({ user, onClose }) => {
  const [modalLoading, setModalLoading] = useState(false)
  const [coursesWithProgress, setCoursesWithProgress] = useState([])
  const [fetchErrors, setFetchErrors] = useState({})
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [showCourseDetails, setShowCourseDetails] = useState(false)
  const [courseParts, setCourseParts] = useState([])
  const [refreshingCourse, setRefreshingCourse] = useState(null)

  // New state for filters and search
  const [partFilter, setPartFilter] = useState("all") // "all", "completed", "inprogress"
  const [partSearchTerm, setPartSearchTerm] = useState("")

  const token = localStorage.getItem("jwttoken")
  // Import jsPDF (add at the top of the file)


  // Add these export functions inside the CourseModal component:

  // Import jsPDF (add at the top of the file)


  // Function to get parts/subcourses details for a course
  // Import jsPDF (add at the top of the file)


  // Helper function to get hierarchical data from coursesWithProgress
  const getHierarchicalCourseData = () => {
    const hierarchicalData = [];

    coursesWithProgress.forEach((course) => {
      if (!course.progressData) return;

      const courseData = {
        courseId: course.course_id,
        courseTitle: course.title,
        courseProgress: `${roundProgress(course.course_progress)}%`,
        completedParts: course.progressData.completed_parts || 0,
        totalParts: course.progressData.total_parts || 0,
        parts: []
      };

      // Process each part
      if (course.progressData.parts && Array.isArray(course.progressData.parts)) {
        course.progressData.parts.forEach((part) => {
          // Calculate part progress
          const partProgress = calculatePartProgress(part);

          const partData = {
            partId: part.part_id,
            partTitle: part.part_title || "Untitled Part",
            partProgress: `${partProgress}%`,
            subparts: []
          };

          // Process each subpart
          if (part.subparts && Array.isArray(part.subparts)) {
            part.subparts.forEach((subpart) => {
              const quizInfo = subpart.quiz || {};

              const subpartData = {
                subpartId: subpart.subpart_id,
                subpartTitle: subpart.title || "Untitled Subpart",
                isCompleted: subpart.is_completed ? "Yes" : "No",
                videoProgress: `${Math.round(subpart.video_progress || 0)}%`,
                hasQuiz: !!subpart.quiz,
                quizTitle: quizInfo.quiz_title || "No Quiz",
                quizStatus: quizInfo.result || "Not Attempted",
                quizScore: quizInfo.score ? `${Number(quizInfo.score).toFixed(2)}%` : "N/A",
                quizSubmittedDate: quizInfo.submitted_at ? formatQuizDate(quizInfo.submitted_at) : "N/A"
              };

              partData.subparts.push(subpartData);
            });
          }

          courseData.parts.push(partData);
        });
      }

      hierarchicalData.push(courseData);
    });

    return hierarchicalData;
  };

  const exportAllCoursesProgressToExcel = () => {
    if (!coursesWithProgress.length) {
      alert("No course data available to export");
      return;
    }

    // Get hierarchical data
    const hierarchicalData = getHierarchicalCourseData();

    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // =========== SHEET 1: COURSE HIERARCHY DETAILS ===========
    const hierarchyData = [];

    // Add report title
    hierarchyData.push(["STUDENT COURSE HIERARCHY REPORT"]);
    hierarchyData.push([""]);

    // Add student info
    hierarchyData.push(["STUDENT INFORMATION"]);
    hierarchyData.push([`Name: ${user["First Name"]} ${user["Last Name"]}`]);
    hierarchyData.push([`Email: ${user.Email}`]);
    hierarchyData.push([`Student ID: ${user.ID}`]);
    hierarchyData.push([`Total Courses: ${hierarchicalData.length}`]);
    hierarchyData.push([""]);

    // Add main headers
    hierarchyData.push([
      "Course",
      "Course Progress",
      "Topic/Part",
      "Topic Progress",
      "Subtopic",
      "Completed",
      "Video Progress",
      "Quiz Title",
      "Quiz Status",
      "Quiz Score",
      "Submitted Date"
    ]);

    // Process each course
    hierarchicalData.forEach((course) => {
      let isFirstRowForCourse = true;

      course.parts.forEach((part) => {
        let isFirstRowForPart = true;

        if (part.subparts.length === 0) {
          // If part has no subparts, add a row for just the part
          hierarchyData.push([
            isFirstRowForCourse ? course.courseTitle : "",
            isFirstRowForCourse ? course.courseProgress : "",
            part.partTitle,
            part.partProgress,
            "No subtopics",
            "",
            "",
            "",
            "",
            "",
            ""
          ]);
          isFirstRowForCourse = false;
        } else {
          // Add each subpart
          part.subparts.forEach((subpart, subpartIndex) => {
            hierarchyData.push([
              isFirstRowForCourse && subpartIndex === 0 ? course.courseTitle : "",
              isFirstRowForCourse && subpartIndex === 0 ? course.courseProgress : "",
              isFirstRowForPart && subpartIndex === 0 ? part.partTitle : "",
              isFirstRowForPart && subpartIndex === 0 ? part.partProgress : "",
              subpart.subpartTitle,
              subpart.isCompleted,
              subpart.videoProgress,
              subpart.quizTitle,
              subpart.quizStatus,
              subpart.quizScore,
              subpart.quizSubmittedDate
            ]);
            isFirstRowForCourse = false;
            isFirstRowForPart = false;
          });
        }
      });

      // Add empty row between courses
      hierarchyData.push(["", "", "", "", "", "", "", "", "", "", ""]);
    });

    // Add timestamp
    hierarchyData.push([""]);
    hierarchyData.push([`Report Generated: ${new Date().toLocaleString()}`]);

    // Create hierarchy worksheet
    const hierarchySheet = XLSX.utils.aoa_to_sheet(hierarchyData);

    // Set column widths
    const hierarchyColWidths = [
      { wch: 25 }, // Course
      { wch: 15 }, // Course Progress
      { wch: 25 }, // Topic/Part
      { wch: 15 }, // Topic Progress
      { wch: 35 }, // Subtopic
      { wch: 12 }, // Completed
      { wch: 15 }, // Video Progress
      { wch: 30 }, // Quiz Title
      { wch: 15 }, // Quiz Status
      { wch: 12 }, // Quiz Score
      { wch: 20 }  // Submitted Date
    ];
    hierarchySheet["!cols"] = hierarchyColWidths;

    // Style headers
    for (let i = 0; i < hierarchyData.length; i++) {
      const row = hierarchyData[i];

      // Style main header
      if (i === 0) {
        for (let C = 0; C < row.length; C++) {
          const address = XLSX.utils.encode_cell({ r: i, c: C });
          if (hierarchySheet[address]) {
            hierarchySheet[address].s = {
              font: { bold: true, size: 14, color: { rgb: "1E40AF" } }
            };
          }
        }
      }

      // Style table headers
      if (row[0] === "Course") {
        for (let C = 0; C < row.length; C++) {
          const address = XLSX.utils.encode_cell({ r: i, c: C });
          if (hierarchySheet[address]) {
            hierarchySheet[address].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "3B82F6" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
        }
      }
    }

    // Add hierarchy sheet to workbook
    XLSX.utils.book_append_sheet(workbook, hierarchySheet, "Course Hierarchy");

    // =========== SHEET 2: SUMMARY STATISTICS ===========
    const summaryData = [];

    // Add title
    summaryData.push(["COURSE PROGRESS SUMMARY"]);
    summaryData.push([""]);

    // Add course summary headers
    summaryData.push([
      "Course Name",
      "Progress",
      "Topics",
      "Completed Topics",
      "Subtopics",
      "Completed Subtopics",
      "Quizzes Available",
      "Quizzes Attempted",
      "Quizzes Passed",
      "Avg Quiz Score"
    ]);

    // Calculate and add summary for each course
    hierarchicalData.forEach((course) => {
      let totalSubparts = 0;
      let completedSubparts = 0;
      let quizzesAvailable = 0;
      let quizzesAttempted = 0;
      let quizzesPassed = 0;
      let totalQuizScore = 0;

      course.parts.forEach((part) => {
        totalSubparts += part.subparts.length;
        completedSubparts += part.subparts.filter(sp => sp.isCompleted === "Yes").length;

        part.subparts.forEach((subpart) => {
          if (subpart.hasQuiz) {
            quizzesAvailable++;
            if (subpart.quizStatus !== "Not Attempted") {
              quizzesAttempted++;
              if (subpart.quizStatus === "Passed") {
                quizzesPassed++;
                totalQuizScore += parseFloat(subpart.quizScore) || 0;
              }
            }
          }
        });
      });

      const topicCompletion = course.totalParts > 0
        ? Math.round((course.completedParts / course.totalParts) * 100)
        : 0;

      const subtopicCompletion = totalSubparts > 0
        ? Math.round((completedSubparts / totalSubparts) * 100)
        : 0;

      const avgQuizScore = quizzesPassed > 0
        ? Math.round((totalQuizScore / quizzesPassed) * 100) / 100
        : 0;

      summaryData.push([
        course.courseTitle,
        course.courseProgress,
        course.totalParts,
        `${course.completedParts} (${topicCompletion}%)`,
        totalSubparts,
        `${completedSubparts} (${subtopicCompletion}%)`,
        quizzesAvailable,
        `${quizzesAttempted} (${quizzesAvailable > 0 ? Math.round((quizzesAttempted / quizzesAvailable) * 100) : 0}%)`,
        `${quizzesPassed} (${quizzesAttempted > 0 ? Math.round((quizzesPassed / quizzesAttempted) * 100) : 0}%)`,
        `${avgQuizScore}%`
      ]);
    });

    // Add overall totals
    summaryData.push([""]);
    summaryData.push(["OVERALL TOTALS"]);

    let grandTotalCourses = hierarchicalData.length;
    let grandTotalParts = 0;
    let grandCompletedParts = 0;
    let grandTotalSubparts = 0;
    let grandCompletedSubparts = 0;
    let grandQuizzesAvailable = 0;
    let grandQuizzesAttempted = 0;
    let grandQuizzesPassed = 0;
    let grandTotalQuizScore = 0;

    hierarchicalData.forEach((course) => {
      grandTotalParts += course.totalParts;
      grandCompletedParts += course.completedParts;

      course.parts.forEach((part) => {
        grandTotalSubparts += part.subparts.length;
        grandCompletedSubparts += part.subparts.filter(sp => sp.isCompleted === "Yes").length;

        part.subparts.forEach((subpart) => {
          if (subpart.hasQuiz) {
            grandQuizzesAvailable++;
            if (subpart.quizStatus !== "Not Attempted") {
              grandQuizzesAttempted++;
              if (subpart.quizStatus === "Passed") {
                grandQuizzesPassed++;
                grandTotalQuizScore += parseFloat(subpart.quizScore) || 0;
              }
            }
          }
        });
      });
    });

    const overallAvgQuizScore = grandQuizzesPassed > 0
      ? Math.round((grandTotalQuizScore / grandQuizzesPassed) * 100) / 100
      : 0;

    summaryData.push([
      `All Courses (${grandTotalCourses})`,
      `${Math.round(hierarchicalData.reduce((sum, c) => sum + parseFloat(c.courseProgress), 0) / grandTotalCourses)}%`,
      grandTotalParts,
      `${grandCompletedParts} (${Math.round((grandCompletedParts / grandTotalParts) * 100)}%)`,
      grandTotalSubparts,
      `${grandCompletedSubparts} (${Math.round((grandCompletedSubparts / grandTotalSubparts) * 100)}%)`,
      grandQuizzesAvailable,
      `${grandQuizzesAttempted} (${Math.round((grandQuizzesAttempted / grandQuizzesAvailable) * 100)}%)`,
      `${grandQuizzesPassed} (${Math.round((grandQuizzesPassed / grandQuizzesAttempted) * 100)}%)`,
      `${overallAvgQuizScore}%`
    ]);

    // Add timestamp
    summaryData.push([""]);
    summaryData.push([`Report Generated: ${new Date().toLocaleString()}`]);

    // Create summary worksheet
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

    // Set column widths for summary
    const summaryColWidths = [
      { wch: 30 }, // Course Name
      { wch: 12 }, // Progress
      { wch: 10 }, // Topics
      { wch: 20 }, // Completed Topics
      { wch: 12 }, // Subtopics
      { wch: 22 }, // Completed Subtopics
      { wch: 18 }, // Quizzes Available
      { wch: 20 }, // Quizzes Attempted
      { wch: 18 }, // Quizzes Passed
      { wch: 15 }  // Avg Quiz Score
    ];
    summarySheet["!cols"] = summaryColWidths;

    // Style summary headers
    for (let i = 0; i < summaryData.length; i++) {
      const row = summaryData[i];

      if (row[0] === "Course Name") {
        for (let C = 0; C < row.length; C++) {
          const address = XLSX.utils.encode_cell({ r: i, c: C });
          if (summarySheet[address]) {
            summarySheet[address].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "10B981" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
        }
      }
    }

    // Add summary sheet to workbook
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary Statistics");

    // Generate filename and save
    const fileName = `${user["First Name"]}_${user["Last Name"]}_Course_Hierarchy_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    // Show success message
    alert(`Course hierarchy report exported to ${fileName}`);
  };

  const exportAllCoursesProgressToPDF = () => {
    if (!coursesWithProgress.length) {
      alert("No course data available to export");
      return;
    }

    // Get hierarchical data
    const hierarchicalData = getHierarchicalCourseData();

    // Create new PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    let yPosition = 20;
    let currentPage = 1;

    // Add title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("COURSE HIERARCHY PROGRESS REPORT", pageWidth / 2, yPosition, { align: "center" });
    yPosition += 10;

    // Add student info
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Student: ${user["First Name"]} ${user["Last Name"]}`, 14, yPosition);
    doc.text(`Email: ${user.Email}`, pageWidth / 2, yPosition);
    yPosition += 7;
    doc.text(`Student ID: ${user.ID}`, 14, yPosition);
    doc.text(`Total Courses: ${hierarchicalData.length}`, pageWidth / 2, yPosition);
    yPosition += 7;
    doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, yPosition);
    yPosition += 15;

    // Add horizontal line
    doc.setLineWidth(0.5);
    doc.line(14, yPosition, pageWidth - 14, yPosition);
    yPosition += 10;

    // Process each course
    hierarchicalData.forEach((course, courseIndex) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
        currentPage++;
      }

      // Course header
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Course ${courseIndex + 1}: ${course.courseTitle}`, 14, yPosition);
      yPosition += 8;

      // Course progress and stats
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Overall Progress: ${course.courseProgress}`, 14, yPosition);
      doc.text(`Topics: ${course.completedParts}/${course.totalParts} completed`, pageWidth / 2, yPosition);
      yPosition += 15;

      // Process each part
      course.parts.forEach((part, partIndex) => {
        // Check if we need a new page for this part
        if (yPosition > pageHeight - 80) {
          doc.addPage();
          yPosition = 20;
          currentPage++;
        }

        // Part header
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(`Topic ${partIndex + 1}: ${part.partTitle}`, 14, yPosition);
        yPosition += 7;

        // Part progress
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Topic Progress: ${part.partProgress}`, 14, yPosition);
        yPosition += 10;

        // Check if there are subparts
        if (part.subparts.length === 0) {
          doc.text("No subtopics available for this topic", 20, yPosition);
          yPosition += 10;
        } else {
          // Create table for subparts
          const subpartHeaders = [
            ["Subtopic", "Completed", "Progress", "Quiz", "Score", "Status"]
          ];

          const subpartData = part.subparts.map((subpart) => {
            return [
              subpart.subpartTitle,
              subpart.isCompleted,
              subpart.videoProgress,
              subpart.quizTitle,
              subpart.quizScore,
              subpart.quizStatus
            ];
          });

          // Add subparts table
          doc.autoTable({
            startY: yPosition,
            head: subpartHeaders,
            body: subpartData,
            theme: "grid",
            headStyles: {
              fillColor: [59, 130, 246],
              textColor: [255, 255, 255],
              fontSize: 9,
              fontStyle: 'bold'
            },
            styles: {
              fontSize: 8,
              cellPadding: 3,
              overflow: 'linebreak'
            },
            columnStyles: {
              0: { cellWidth: 45 }, // Subtopic
              1: { cellWidth: 15, halign: 'center' }, // Completed
              2: { cellWidth: 15, halign: 'center' }, // Progress
              3: { cellWidth: 30 }, // Quiz
              4: { cellWidth: 12, halign: 'center' }, // Score
              5: { cellWidth: 15, halign: 'center' }  // Status
            },
            margin: { left: 14, right: 14 },
            didDrawCell: function (data) {
              // Color code the completed column
              if (data.column.index === 1 && data.cell.raw === "Yes") {
                doc.setFillColor(34, 197, 94); // Green
                doc.setDrawColor(34, 197, 94);
                doc.rect(data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2, 'F');

                doc.setTextColor(255, 255, 255);
                doc.text("Yes", data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1, {
                  align: 'center',
                  baseline: 'middle'
                });

                doc.setTextColor(0, 0, 0);
              }

              // Color code the quiz status column
              if (data.column.index === 5 && data.cell.raw) {
                const status = data.cell.raw;
                let color;

                if (status === "Passed") {
                  color = [34, 197, 94]; // Green
                } else if (status === "Failed") {
                  color = [239, 68, 68]; // Red
                } else if (status === "Not Attempted") {
                  color = [107, 114, 128]; // Gray
                } else {
                  color = [245, 158, 11]; // Orange
                }

                doc.setFillColor(...color);
                doc.setDrawColor(...color);
                doc.rect(data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2, 'F');

                doc.setTextColor(255, 255, 255);
                doc.text(status, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1, {
                  align: 'center',
                  baseline: 'middle'
                });

                doc.setTextColor(0, 0, 0);
              }
            }
          });

          yPosition = doc.lastAutoTable.finalY + 10;
        }

        // Add separator between parts (if not last part)
        if (partIndex < course.parts.length - 1) {
          doc.setLineWidth(0.2);
          doc.setDrawColor(200, 200, 200);
          doc.line(14, yPosition, pageWidth - 14, yPosition);
          yPosition += 10;
        }
      });

      // Add separator between courses (if not last course)
      if (courseIndex < hierarchicalData.length - 1) {
        yPosition += 10;
        doc.setLineWidth(0.5);
        doc.setDrawColor(100, 100, 100);
        doc.line(14, yPosition, pageWidth - 14, yPosition);
        yPosition += 15;
      }
    });

    // Add summary page
    doc.addPage();
    yPosition = 20;

    // Add summary title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("PROGRESS SUMMARY", pageWidth / 2, yPosition, { align: "center" });
    yPosition += 15;

    // Calculate overall statistics
    let totalCourses = hierarchicalData.length;
    let totalParts = 0;
    let completedParts = 0;
    let totalSubparts = 0;
    let completedSubparts = 0;
    let quizzesAvailable = 0;
    let quizzesAttempted = 0;
    let quizzesPassed = 0;
    let totalQuizScore = 0;

    hierarchicalData.forEach(course => {
      totalParts += course.totalParts;
      completedParts += course.completedParts;

      course.parts.forEach(part => {
        totalSubparts += part.subparts.length;
        completedSubparts += part.subparts.filter(sp => sp.isCompleted === "Yes").length;

        part.subparts.forEach(subpart => {
          if (subpart.hasQuiz) {
            quizzesAvailable++;
            if (subpart.quizStatus !== "Not Attempted") {
              quizzesAttempted++;
              if (subpart.quizStatus === "Passed") {
                quizzesPassed++;
                totalQuizScore += parseFloat(subpart.quizScore) || 0;
              }
            }
          }
        });
      });
    });

    const avgCourseProgress = Math.round(
      hierarchicalData.reduce((sum, course) => {
        const progress = parseFloat(course.courseProgress) || 0;
        return sum + progress;
      }, 0) / totalCourses
    );

    const partsCompletionRate = totalParts > 0 ? Math.round((completedParts / totalParts) * 100) : 0;
    const subpartsCompletionRate = totalSubparts > 0 ? Math.round((completedSubparts / totalSubparts) * 100) : 0;
    const quizAttemptRate = quizzesAvailable > 0 ? Math.round((quizzesAttempted / quizzesAvailable) * 100) : 0;
    const quizPassRate = quizzesAttempted > 0 ? Math.round((quizzesPassed / quizzesAttempted) * 100) : 0;
    const avgQuizScore = quizzesPassed > 0 ? Math.round((totalQuizScore / quizzesPassed) * 100) / 100 : 0;

    // Create summary table
    const summaryHeaders = [["Metric", "Value", "Details"]];
    const summaryData = [
      ["Total Courses", totalCourses.toString(), ""],
      ["Total Topics", totalParts.toString(), ""],
      ["Topics Completed", `${completedParts} (${partsCompletionRate}%)`, ""],
      ["Total Subtopics", totalSubparts.toString(), ""],
      ["Subtopics Completed", `${completedSubparts} (${subpartsCompletionRate}%)`, ""],
      ["", "", ""],
      ["Average Course Progress", `${avgCourseProgress}%`, ""],
      ["Quizzes Available", quizzesAvailable.toString(), ""],
      ["Quizzes Attempted", `${quizzesAttempted} (${quizAttemptRate}%)`, ""],
      ["Quizzes Passed", `${quizzesPassed} (${quizPassRate}%)`, ""],
      ["Average Quiz Score", `${avgQuizScore}%`, ""]
    ];

    doc.autoTable({
      startY: yPosition,
      head: summaryHeaders,
      body: summaryData,
      theme: "grid",
      headStyles: {
        fillColor: [16, 185, 129],
        textColor: [255, 255, 255],
        fontSize: 11,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 10,
        cellPadding: 5,
        overflow: 'linebreak'
      },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: 'bold' }, // Metric
        1: { cellWidth: 40, halign: 'center' }, // Value
        2: { cellWidth: 40 }  // Details
      },
      margin: { left: 14, right: 14 }
    });

    yPosition = doc.lastAutoTable.finalY + 20;

    // Add footer
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text(`Report generated on: ${new Date().toLocaleString()} | Pages: ${currentPage + 1}`, pageWidth / 2, pageHeight - 10, { align: "center" });

    // Generate filename and save
    const fileName = `${user["First Name"]}_${user["Last Name"]}_Course_Hierarchy_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);

    // Show success message
    alert(`Course hierarchy report exported to ${fileName}`);
  };
  /**
   * FIX: This function was modified to handle the API response object.
   * It now prefers the `course_progress` value directly from the API.
   * If that's not available, it calculates it from the `parts` array within the response object.
   */
  const calculateCourseProgress = (progressData) => {
    if (!progressData) return 0

    // Prefer the progress value from the API response if it exists
    if (typeof progressData.course_progress === "number") {
      return progressData.course_progress
    }

    // Fallback to manual calculation if course_progress is not in the response
    const parts = progressData.parts
    if (!Array.isArray(parts)) return 0

    let totalSubparts = 0
    let completedSubparts = 0

    parts.forEach((part) => {
      if (part.subparts && Array.isArray(part.subparts)) {
        totalSubparts += part.subparts.length
        completedSubparts += part.subparts.filter((subpart) => subpart.is_completed).length
      }
    })

    return totalSubparts > 0 ? Math.round((completedSubparts / totalSubparts) * 100) : 0
  }

  const calculatePartProgress = (part) => {
    if (!part.subparts || !Array.isArray(part.subparts)) return 0

    const totalSubparts = part.subparts.length
    const totalProgress = part.subparts.reduce(
      (acc, subpart) => acc + (subpart.video_progress || 0),
      0,
    )

    return totalSubparts > 0 ? Math.round(totalProgress / totalSubparts) : 0
  }

  const fetchCourseProgress = async (courseId, userId) => {
    try {
      const cacheKey = `${userId}-${courseId}`
      console.log(" Fetching progress for user:", userId, "course:", courseId)

      // Check cache first
      if (COURSE_PROGRESS_CACHE.has(cacheKey)) {
        console.log(" Returning cached progress for key:", cacheKey)
        return COURSE_PROGRESS_CACHE.get(cacheKey)
      }

      if (!courseId || courseId === "undefined" || !userId) {
        console.log(" Invalid course ID or user ID, skipping:", courseId, userId)
        return null
      }

      if (!token) {
        console.warn(" No token available for course progress API")
        return []
      }

      const response = await axios.get(
        `https://kaasbackend.vetrinahealthcare.com/lms/admin/student-course-progress/?student_id=${userId}&course_id=${courseId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 30000, // 10 second timeout
        },
      )

      console.log(" Course progress response:", response.data)

      // Cache the response
      COURSE_PROGRESS_CACHE.set(cacheKey, response.data)

      return response.data
    } catch (error) {
      const cacheKey = `${userId}-${courseId}`
      console.error(` Error fetching progress for course ${courseId}:`, error.message)

      // Cache error to prevent repeated calls
      if (error.response?.status === 404 || error.response?.status === 400) {
        COURSE_PROGRESS_CACHE.set(cacheKey, null) // Cache null for errors
      }

      return null // Return null on error
    }
  }

  const refreshCourseProgress = async (courseId) => {
    try {
      setRefreshingCourse(courseId)
      const userId = user.ID
      const cacheKey = `${userId}-${courseId}`

      // Clear cache for this course
      COURSE_PROGRESS_CACHE.delete(cacheKey)

      const updatedProgress = await fetchCourseProgress(courseId, userId)

      if (updatedProgress) {
        // Update the course progress in the modal
        const updatedCourses = coursesWithProgress.map((course) =>
          course.course_id === courseId
            ? {
              ...course,
              progressData: updatedProgress,
              course_progress: calculateCourseProgress(updatedProgress),
              lastUpdated: Date.now(),
            }
            : course,
        )
        setCoursesWithProgress(updatedCourses)

        // Update the selected course
        if (selectedCourse && selectedCourse.course_id === courseId) {
          setSelectedCourse((prev) => ({
            ...prev,
            progressData: updatedProgress,
            course_progress: calculateCourseProgress(updatedProgress),
          }))

          // Calculate progress for each part
          const partsWithProgress = (updatedProgress.parts || []).map((part) => ({
            ...part,
            progress: calculatePartProgress(part),
          }))
          setCourseParts(partsWithProgress)
        }
      }
    } catch (error) {
      console.error(` Error refreshing course progress:`, error)
    } finally {
      setRefreshingCourse(null)
    }
  }

  useEffect(() => {
    const fetchAllCourseProgress = async () => {
      if (!user || !user.rawUserData || !user.rawUserData.courses_enrolled || !user.ID) {
        setModalLoading(false)
        return
      }

      setModalLoading(true)
      setFetchErrors({})

      const courses = user.rawUserData.courses_enrolled
      const userId = user.ID // Get user ID
      const coursesWithProgressData = []
      const errors = {}

      // Process courses sequentially to avoid overwhelming the API
      for (let i = 0; i < courses.length; i++) {
        const course = courses[i]

        try {
          console.log(" Fetching progress for course in modal:", course.course_id)

          // Use Promise.race with timeout to prevent hanging requests
          const progressData = await Promise.race([
            fetchCourseProgress(course.course_id, userId), // Pass both courseId and userId
            new Promise((_, reject) => setTimeout(() => reject(new Error("Request timeout")), 10000)),
          ])

          // Handle case where progressData is null due to fetch error
          if (progressData === null) {
            throw new Error("Failed to fetch progress data.")
          }

          const courseProgress = calculateCourseProgress(progressData)

          coursesWithProgressData.push({
            course_id: course.course_id,
            title: course.title,
            course_progress: courseProgress,
            progressData: progressData, // Store the entire object
            lastUpdated: Date.now(),
          })

          // Small delay between requests to avoid rate limiting
          if (i < courses.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 200))
          }
        } catch (error) {
          console.error(` Failed to fetch progress for course ${course.course_id}:`, error)
          errors[course.course_id] = error.message

          // Add course with zero progress on error
          coursesWithProgressData.push({
            course_id: course.course_id,
            title: course.title,
            course_progress: 0,
            progressData: null,
            error: true,
          })
        }
      }

      setCoursesWithProgress(coursesWithProgressData)
      setFetchErrors(errors)
      setModalLoading(false)
    }

    fetchAllCourseProgress()
  }, [user])

  // Clear cache when modal closes
  useEffect(() => {
    return () => {
      // Optionally clear cache after some time or keep it for session
      // COURSE_PROGRESS_CACHE.clear();
    }
  }, [])

  /**
   * FIX: This function was modified to access the `parts` array within `course.progressData`.
   * `course.progressData` is the object from the API, and `course.progressData.parts` is the array we need.
   * Optional chaining (`?.`) and nullish coalescing (`|| []`) are used for safety.
   */
  const handleCourseClick = (course) => {
    if (course.error || !course.progressData) return // Don't allow clicking on errored or empty courses

    setSelectedCourse(course)

    // Calculate progress for each part
    const partsWithProgress = (course.progressData?.parts || []).map((part) => ({
      ...part,
      progress: calculatePartProgress(part),
    }))

    setCourseParts(partsWithProgress)
    setShowCourseDetails(true)
    // Reset filters when opening course details
    setPartFilter("all")
    setPartSearchTerm("")
  }

  const handleBackToCourses = () => {
    setShowCourseDetails(false)
    setSelectedCourse(null)
    setCourseParts([])
    // Reset filters when going back
    setPartFilter("all")
    setPartSearchTerm("")
  }

  const retryFetchCourseProgress = async (courseId) => {
    setCoursesWithProgress((prev) =>
      prev.map((course) => (course.course_id === courseId ? { ...course, error: false } : course)),
    )

    const userId = user.ID
    const cacheKey = `${userId}-${courseId}`

    // Clear cache and retry
    COURSE_PROGRESS_CACHE.delete(cacheKey)

    try {
      const progressData = await fetchCourseProgress(courseId, userId) // Pass userId here
      if (progressData === null) {
        throw new Error("Failed to fetch progress data on retry.")
      }
      const courseProgress = calculateCourseProgress(progressData)

      setCoursesWithProgress((prev) =>
        prev.map((course) =>
          course.course_id === courseId
            ? {
              ...course,
              course_progress: courseProgress,
              progressData: progressData,
              lastUpdated: Date.now(),
              error: false,
            }
            : course,
        ),
      )
    } catch (error) {
      console.error(` Retry failed for course ${courseId}:`, error)
      setCoursesWithProgress((prev) =>
        prev.map((course) => (course.course_id === courseId ? { ...course, error: true } : course)),
      )
    }
  }

  // Filter course parts based on filter and search term
  const getFilteredCourseParts = () => {
    if (!courseParts || courseParts.length === 0) return []

    return courseParts.map(part => {
      // Filter subparts based on search term and filter
      const filteredSubparts = part.subparts?.filter(subpart => {
        // Apply search filter
        const matchesSearch = partSearchTerm === "" ||
          subpart.title?.toLowerCase().includes(partSearchTerm.toLowerCase()) ||
          part.part_title?.toLowerCase().includes(partSearchTerm.toLowerCase())

        // Apply status filter
        const matchesFilter =
          partFilter === "all" ||
          (partFilter === "completed" && subpart.is_completed) ||
          (partFilter === "inprogress" && !subpart.is_completed)

        return matchesSearch && matchesFilter
      }) || []

      // Return part with filtered subparts
      return {
        ...part,
        subparts: filteredSubparts
      }
    }).filter(part => part.subparts.length > 0) // Only show parts that have subparts after filtering
  }

  const filteredCourseParts = getFilteredCourseParts()

  if (!user) return null

  const roundProgress = (progress) => {
    if (typeof progress !== "number" || isNaN(progress)) return 0
    return Math.round(progress)
  }

  const formatQuizDate = (dateString) => {
    if (!dateString) return ""
    return new Date(dateString).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          width: "90%",
          maxWidth: "1000px",
          height: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Fixed Header Section */}
        {/* Fixed Header Section */}
        {/* Fixed Header Section */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e2e8f0",
            padding: "15px 30px",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            {showCourseDetails && (
              <button
                onClick={handleBackToCourses}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#64748b",
                }}
              >
                <ChevronLeft style={{ width: "16px", height: "16px" }} />
                Back
              </button>
            )}
            <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#1e293b", margin: 0 }}>
              {!showCourseDetails
                ? `Course Details for ${user["First Name"]} ${user["Last Name"]}`
                : `${selectedCourse?.title}`}
            </h2>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* Export Buttons - Only show when NOT in course details view (i.e., showing all courses) */}
            {!showCourseDetails && coursesWithProgress.length > 0 && (
              <div style={{ display: "flex", gap: "8px", marginRight: "10px" }}>
                {/* Excel Export Button for All Courses */}
                <button
                  onClick={() => exportAllCoursesProgressToExcel()}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 12px",
                    backgroundColor: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                  title="Download All Courses Progress (Excel)"
                >
                  <Download style={{ width: "16px", height: "16px" }} />
                  Excel
                </button>

                {/* PDF Export Button for All Courses */}
                {/* <button
          onClick={() => exportAllCoursesProgressToPDF()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 12px",
            backgroundColor: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
          }}
          title="Download All Courses Progress (PDF)"
        >
          <FileText style={{ width: "16px", height: "16px" }} />
          PDF
        </button> */}
              </div>
            )}

            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "24px",
                color: "#64748b",
                padding: "8px",
              }}
            >
              ×
            </button>
          </div>
        </div>
        {/* Scrollable Modal Content */}
        <div style={{ overflowY: "auto", padding: "20px 30px" }}>
          {!showCourseDetails ? (
            <>
              <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", gap: "20px", marginBottom: "15px" }}>
                  <div>
                    <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>User ID</p>
                    <p style={{ fontSize: "14px", fontWeight: "500" }}>{user.ID}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Occupation</p>
                    <p style={{ fontSize: "14px", fontWeight: "500" }}>{user.Occupation}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Email</p>
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      {user.Email}
                    </p>
                  </div>
                </div>
              </div>

              <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#1e293b", marginBottom: "15px" }}>
                Enrolled Courses ({coursesWithProgress.length})
              </h3>

              {modalLoading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <div style={{ fontSize: "14px", color: "#64748b" }}>Loading course progress...</div>
                </div>
              ) : coursesWithProgress.length > 0 ? (
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  {coursesWithProgress.map((course, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "15px",
                        borderBottom: index < coursesWithProgress.length - 1 ? "1px solid #e2e8f0" : "none",
                        cursor: course.error ? "default" : "pointer",
                        transition: "background-color 0.2s",
                        backgroundColor: course.error ? "#fef2f2" : "transparent",
                      }}
                      onMouseEnter={(e) => !course.error && (e.currentTarget.style.backgroundColor = "#f1f5f9")}
                      onMouseLeave={(e) => !course.error && (e.currentTarget.style.backgroundColor = "transparent")}
                      onClick={() => handleCourseClick(course)}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <p style={{ fontWeight: "500", marginBottom: "5px" }}>{course.title}</p>
                          <p style={{ fontSize: "12px", color: "#64748b" }}>Course ID: {course.course_id}</p>
                          {course.error && (
                            <p style={{ fontSize: "12px", color: "#ef4444", marginTop: "5px" }}>
                              Failed to load progress
                            </p>
                          )}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          {course.error ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                retryFetchCourseProgress(course.course_id)
                              }}
                              style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                backgroundColor: "#3b82f6",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            >
                              Retry
                            </button>
                          ) : (
                            <>
                              <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Progress</p>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <div
                                  style={{
                                    width: "100px",
                                    height: "8px",
                                    backgroundColor: "#e2e8f0",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${roundProgress(course.course_progress)}%`,
                                      height: "100%",
                                      backgroundColor:
                                        roundProgress(course.course_progress) >= 80
                                          ? "#10b981"
                                          : roundProgress(course.course_progress) >= 50
                                            ? "#f59e0b"
                                            : "#3b82f6",
                                      borderRadius: "4px",
                                    }}
                                  ></div>
                                </div>
                                <span style={{ fontSize: "14px", fontWeight: "600" }}>
                                  {roundProgress(course.course_progress)}%
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  <p style={{ color: "#64748b" }}>No courses enrolled</p>
                </div>
              )}
            </>
          ) : (
            <div>
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                  }}
                >
                  <div>
                    <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Course ID</p>
                    <p style={{ fontSize: "14px", fontWeight: "500" }}>{selectedCourse?.course_id}</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Overall Progress</p>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div
                        style={{
                          width: "120px",
                          height: "8px",
                          backgroundColor: "#e2e8f0",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${roundProgress(selectedCourse?.course_progress || 0)}%`,
                            height: "100%",
                            backgroundColor:
                              roundProgress(selectedCourse?.course_progress || 0) >= 80
                                ? "#10b981"
                                : roundProgress(selectedCourse?.course_progress || 0) >= 50
                                  ? "#f59e0b"
                                  : "#3b82f6",
                            borderRadius: "4px",
                          }}
                        ></div>
                      </div>
                      <span style={{ fontSize: "16px", fontWeight: "600" }}>
                        {roundProgress(selectedCourse?.course_progress || 0)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search and Filter Section for Course Parts */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  gap: "15px",
                  flexWrap: "wrap",
                }}
              >
                <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#1e293b", margin: 0 }}>
                  Course Parts & Modules
                  {filteredCourseParts.length !== courseParts.length && (
                    <span style={{ fontSize: "14px", color: "#64748b", fontWeight: "normal", marginLeft: "8px" }}>
                      (Showing {filteredCourseParts.length} of {courseParts.length})
                    </span>
                  )}
                </h3>

                <div style={{ display: "flex", gap: "15px", alignItems: "center", flexWrap: "wrap" }}>
                  {/* Search Bar */}
                  <div style={{ position: "relative", minWidth: "250px" }}>
                    <Search
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#94a3b8",
                        width: "16px",
                        height: "16px",
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Search parts and modules..."
                      value={partSearchTerm}
                      onChange={(e) => setPartSearchTerm(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 12px 10px 40px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.2s",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                      onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                    />
                  </div>

                  {/* Filter Dropdown */}
                  <div style={{ position: "relative" }}>
                    <select
                      value={partFilter}
                      onChange={(e) => setPartFilter(e.target.value)}
                      style={{
                        padding: "10px 35px 10px 12px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                        backgroundColor: "white",
                        cursor: "pointer",
                        appearance: "none",
                        minWidth: "150px",
                      }}
                    >
                      <option value="all">All Modules</option>
                      <option value="completed">Completed</option>
                      <option value="inprogress">In Progress</option>
                    </select>
                    <ChevronDown
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#64748b",
                        width: "16px",
                        height: "16px",
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                </div>
              </div>

              {filteredCourseParts && filteredCourseParts.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  {filteredCourseParts.map((part, partIndex) => (
                    <div
                      key={partIndex}
                      style={{
                        backgroundColor: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        overflow: "hidden",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
                      }}
                    >
                      {/* ---- Part Header ---- */}
                      <div
                        style={{
                          padding: "15px 20px",
                          backgroundColor: "white",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}>{part.part_title}</h4>
                          <span
                            style={{
                              fontSize: "12px",
                              padding: "4px 12px",
                              borderRadius: "12px",
                              backgroundColor:
                                roundProgress(part.progress) >= 80
                                  ? "#dcfce7"
                                  : roundProgress(part.progress) >= 50
                                    ? "#fef3c7"
                                    : "#dbeafe",
                              color:
                                roundProgress(part.progress) >= 80
                                  ? "#166534"
                                  : roundProgress(part.progress) >= 50
                                    ? "#92400e"
                                    : "#1d4ed8",
                              fontWeight: "500",
                            }}
                          >
                            {roundProgress(part.progress)}% Complete
                          </span>
                        </div>
                      </div>

                      {/* ---- Subparts ---- */}
                      {part.subparts && part.subparts.length > 0 && (
                        <div style={{ padding: "15px 20px" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {part.subparts.map((subpart, subpartIndex) => (
                              <div
                                key={subpartIndex}
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "6px",
                                  padding: "12px 15px",
                                  backgroundColor: "white",
                                  borderRadius: "6px",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                {/* ---- Title Row ---- */}
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <div
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        borderRadius: "50%",
                                        backgroundColor: subpart.is_completed ? "#10b981" : "#e2e8f0",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {subpart.is_completed && (
                                        <Check style={{ width: "12px", height: "12px", color: "white" }} />
                                      )}
                                    </div>

                                    <span
                                      style={{
                                        fontSize: "13px",
                                        color: subpart.is_completed ? "#374151" : "#1e293b",
                                        textDecoration: subpart.is_completed ? "line-through" : "none",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {subpart.title}
                                    </span>
                                  </div>

                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: subpart.is_completed ? "#10b981" : "#f59e0b",
                                      fontWeight: "500",
                                    }}
                                  >
                                    {subpart.is_completed ? "Completed" : "In Progress"}
                                  </span>
                                </div>

                                {/* ---- Video Progress ---- */}
                                {!subpart.is_completed && subpart.video_progress > 0 && (
                                  <div style={{ marginTop: "4px" }}>
                                    <div
                                      style={{
                                        width: "100%",
                                        height: "6px",
                                        backgroundColor: "#e2e8f0",
                                        borderRadius: "3px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: `${Math.round(subpart.video_progress)}%`,
                                          height: "100%",
                                          backgroundColor: "#f59e0b",
                                          borderRadius: "3px",
                                          transition: "width 0.3s ease",
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#64748b",
                                        marginTop: "3px",
                                        textAlign: "right",
                                      }}
                                    >
                                      Progress: {Math.round(subpart.video_progress)}%
                                    </div>
                                  </div>
                                )}

                                {/* ---- Quiz Information ---- */}
                                <div
                                  style={{
                                    marginTop: "10px",
                                    padding: "12px",
                                    backgroundColor: "#f8fafc",
                                    borderRadius: "6px",
                                    border: "1px solid #e2e8f0",
                                  }}
                                >
                                  {subpart.quiz ? (
                                    // Quiz data is available
                                    subpart.quiz.result ? (
                                      // Quiz has been taken and has a result
                                      <div>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "10px",
                                          }}
                                        >
                                          <p
                                            style={{
                                              fontWeight: "600",
                                              fontSize: "13px",
                                              color: "#374151",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "6px",
                                            }}
                                          >
                                            <HelpCircle style={{ width: "14px", height: "14px" }} />
                                            {subpart.quiz.quiz_title}
                                          </p>
                                          <span
                                            style={{
                                              fontSize: "12px",
                                              padding: "4px 10px",
                                              borderRadius: "12px",
                                              fontWeight: "500",
                                              backgroundColor:
                                                subpart.quiz.result === "Passed" ? "#dcfce7" : "#fee2e2",
                                              color: subpart.quiz.result === "Passed" ? "#166534" : "#b91c1c",
                                            }}
                                          >
                                            {subpart.quiz.result}
                                          </span>
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "12px",
                                              color: "#64748b",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "4px",
                                            }}
                                          >
                                            <Star style={{ width: "12px", height: "12px", color: "#f59e0b" }} />
                                            Score: <strong>{Number(subpart.quiz.score).toFixed(2)}%</strong>
                                          </span>

                                          <span style={{ fontSize: "11px", color: "#64748b" }}>
                                            Submitted: {formatQuizDate(subpart.quiz.submitted_at)}
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      // Quiz exists but has not been taken
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <p
                                          style={{
                                            fontWeight: "500",
                                            fontSize: "13px",
                                            color: "#374151",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                          }}
                                        >
                                          <HelpCircle style={{ width: "14px", height: "14px" }} />
                                          {subpart.quiz.quiz_title}
                                        </p>
                                        <span
                                          style={{
                                            fontSize: "12px",
                                            padding: "4px 10px",
                                            borderRadius: "12px",
                                            backgroundColor: "#f1f5f9",
                                            color: "#64748b",
                                            fontWeight: "500",
                                          }}
                                        >
                                          Not Attempted
                                        </span>
                                      </div>
                                    )
                                  ) : (
                                    // No quiz for this subpart
                                    <p style={{ fontSize: "12px", color: "#64748b", textAlign: "center" }}>
                                      No quiz available for this subpart
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  <p style={{ color: "#64748b" }}>
                    {courseParts.length === 0
                      ? "No course parts available"
                      : "No modules match your current filters"}
                  </p>
                  {(partFilter !== "all" || partSearchTerm) && (
                    <button
                      onClick={() => {
                        setPartFilter("all")
                        setPartSearchTerm("")
                      }}
                      style={{
                        marginTop: "10px",
                        padding: "8px 16px",
                        backgroundColor: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fixed Footer Section */}
        <div
          style={{
            marginTop: "auto",
            padding: "15px 30px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              backgroundColor: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// Memoized version of CourseModal
const OptimizedCourseModal = React.memo(CourseModal)

// Rest of the Dashboard component remains exactly the same...
const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("overview")
  const [activeSection, setActiveSection] = useState("reports")
  const [searchTerm, setSearchTerm] = useState("")
  const [filters, setFilters] = useState({
    occupation: "",
    division: "",
    subDivision: "",
    designation: "",
    companyEmployee: "",
    courseStatus: "",
    certificateStatus: "",
  })
  const [showFilters, setShowFilters] = useState(false)
  const [dateRange, setDateRange] = useState("month")
  const [showExportOptions, setShowExportOptions] = useState(false)
  const [apiData, setApiData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [selectedUser, setSelectedUser] = useState(null)
  const [showCourseModal, setShowCourseModal] = useState(false)

  const token = localStorage.getItem("jwttoken")
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`

  // New state for dropdown options
  const [occupationOptions, setOccupationOptions] = useState([])
  const [divisionOptions, setDivisionOptions] = useState([])
  const [designationOptions, setDesignationOptions] = useState([])
  const [subDivisionOptions, setSubDivisionOptions] = useState([])

  // Fetch dropdown data from APIs
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        if (!token) {
          console.warn(" No JWT token found in localStorage")
          // Set default options to prevent errors
          setOccupationOptions(["Farmer", "Doctor", "Company employee", "Business", "Consultant", "Service"])
          setDivisionOptions(["North", "South", "East", "West", "Central"])
          setDesignationOptions(["Manager", "Executive", "Senior Executive", "Assistant Manager", "Director"])
          setSubDivisionOptions(["Sub-North", "Sub-South", "Sub-East", "Sub-West"])
          return
        }

        // Fetch occupations
        try {
          const occupationRes = await axios.get("https://kaasbackend.vetrinahealthcare.com/master/occupations/")
          setOccupationOptions(occupationRes.data.map((item) => item.name))
        } catch (err) {
          console.warn(" Failed to fetch occupations, using defaults:", err.message)
          setOccupationOptions(["Farmer", "Doctor", "Company employee", "Business", "Consultant", "Service"])
        }

        // Fetch divisions
        try {
          const divisionRes = await axios.get("https://kaasbackend.vetrinahealthcare.com/master/divisions/")
          setDivisionOptions(divisionRes.data.map((item) => item.name))
        } catch (err) {
          console.warn(" Failed to fetch divisions, using defaults:", err.message)
          setDivisionOptions(["North", "South", "East", "West", "Central"])
        }

        // Fetch designations
        try {
          const designationRes = await axios.get("https://kaasbackend.vetrinahealthcare.com/master/designations/")
          setDesignationOptions(designationRes.data.map((item) => item.name))
        } catch (err) {
          console.warn(" Failed to fetch designations, using defaults:", err.message)
          setDesignationOptions(["Manager", "Executive", "Senior Executive", "Assistant Manager", "Director"])
        }

        // Fetch subdivisions
        try {
          const subDivisionRes = await axios.get("https://kaasbackend.vetrinahealthcare.com/master/subdivisions/")
          setSubDivisionOptions(subDivisionRes.data.map((item) => item.name))
        } catch (err) {
          console.warn(" Failed to fetch subdivisions, using defaults:", err.message)
          setSubDivisionOptions(["Sub-North", "Sub-South", "Sub-East", "Sub-West"])
        }
      } catch (err) {
        console.error(" Error in fetchDropdownData:", err)
        // Set fallback options
        setOccupationOptions(["Farmer", "Doctor", "Company employee", "Business", "Consultant", "Service"])
        setDivisionOptions(["North", "South", "East", "West", "Central"])
        setDesignationOptions(["Manager", "Executive", "Senior Executive", "Assistant Manager", "Director"])
        setSubDivisionOptions(["Sub-North", "Sub-South", "Sub-East", "Sub-West"])
      }
    }

    fetchDropdownData()
  }, [token])

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!token) {
          setError("Authentication required. Please log in to access the dashboard.")
          setLoading(false)
          return
        }

        const response = await fetch("https://kaasbackend.vetrinahealthcare.com/lms/reports/students/json/", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        })

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("Authentication failed. Please check your login credentials.")
          }
          throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`)
        }

        const data = await response.json()
        console.log(" Raw API data:", data)
        setApiData(data)
        setLoading(false)
      } catch (err) {
        console.error(" Error fetching student data:", err)
        setError(err.message)
        setLoading(false)
      }
    }

    fetchData()
  }, [token])

  // Helper function to format date properly
  const formatApiDate = (dateString) => {
    // 1. Handle null, empty, or invalid inputs
    if (!dateString || dateString === "None" || dateString === "") {
      return "N/A"
    }

    try {
      // 2. Isolate the date part from the string (e.g., "06-05-2025 07:11:01" -> "06-05-2025")
      const datePart = dateString.split(" ")[0]

      // 3. Split the date part into day, month, and year
      const [day, month, year] = datePart.split("-")

      // 4. Validate that we have three parts and they look like a date
      if (!day || !month || !year || year.length !== 4) {
        // If format is not DD-MM-YYYY, try a fallback for other potential formats
        const fallbackDate = new Date(dateString)
        if (!isNaN(fallbackDate.getTime())) {
          return fallbackDate.toISOString().split("T")[0]
        }
        return "N/A"
      }

      // 5. Reassemble into the universal YYYY-MM-DD format
      const isoFormattedDateString = `${year}-${month}-${day}` // "2025-05-06"

      // 6. Create a final Date object to ensure it's valid before returning
      const date = new Date(isoFormattedDateString)
      if (isNaN(date.getTime())) {
        return "N/A" // Handles cases like "32-05-2025"
      }

      // 7. Return the correctly formatted string
      return isoFormattedDateString
    } catch (error) {
      console.error("Error formatting date:", dateString, error)
      return "N/A" // Return "N/A" if any error occurs
    }
  }

  // Generate realistic registration dates
  const generateRegistrationDate = (index, joinDate) => {
    if (joinDate && joinDate !== "N/A") return joinDate
    const endDate = new Date()
    const startDate = new Date()
    startDate.setFullYear(endDate.getFullYear() - 2)
    const randomTime = startDate.getTime() + Math.random() * (endDate.getTime() - startDate.getTime())
    return new Date(randomTime).toISOString().split("T")[0]
  }

  // Generate account created date (slightly different from registration date)
  const generateAccountCreatedDate = (registrationDate, index) => {
    if (registrationDate === "N/A") {
      const endDate = new Date()
      const startDate = new Date()
      startDate.setFullYear(endDate.getFullYear() - 2)
      const randomTime = startDate.getTime() + Math.random() * (endDate.getTime() - startDate.getTime())
      return new Date(randomTime).toISOString().split("T")[0]
    }
    // Account created date should be same or slightly after registration
    const regDate = new Date(registrationDate)
    const accountDate = new Date(regDate)
    // Add 0-7 days to registration date for account creation
    accountDate.setDate(regDate.getDate() + Math.floor(Math.random() * 8))
    return accountDate.toISOString().split("T")[0]
  }

  // Generate realistic status based on user activity
  const generateUserStatus = (index, joinDate, progress) => {
    const statuses = ["Active", "Inactive", "Suspended"]
    const progressNum = Number.parseInt(progress) || 0
    // More likely to be active if they have progress
    if (progressNum > 50) {
      return Math.random() > 0.8 ? statuses[Math.floor(Math.random() * 3)] : "Active"
    } else if (progressNum > 20) {
      return Math.random() > 0.6 ? statuses[Math.floor(Math.random() * 3)] : "Active"
    } else {
      // Lower progress users more likely to be inactive
      const weights = [0.4, 0.5, 0.1] // Active, Inactive, Suspended
      const random = Math.random()
      if (random < weights[0]) return "Active"
      if (random < weights[0] + weights[1]) return "Inactive"
      return "Suspended"
    }
  }

  const transformData = (data) => {
    console.log(" Transforming data:", data)
    const safe = (value) => {
      if (!value || value === "" || value === undefined || value === null) return "N/A";
      return value;
    };
    const yesNo = (value) => {
      if (!value) return "N/A";       // empty → N/A
      if (value === "Yes") return "Yes";
      if (value === "No") return "No";
      return "N/A"; // unexpected values
    };

    return data.map((user, index) => {
      const formattedJoinDate = formatApiDate(user.join_date)
      const registrationDate = generateRegistrationDate(index, formattedJoinDate)
      const accountCreatedDate = generateRegistrationDate(index, formattedJoinDate)

      let coursesEnrolled = []
      let courseNames = "N/A"

      if (user.courses_enrolled && Array.isArray(user.courses_enrolled) && user.courses_enrolled.length > 0) {
        coursesEnrolled = user.courses_enrolled.map((course) => ({
          course_id: course.course_id,
          title: course.title,
        }))
        courseNames = user.courses_enrolled.map((c) => c.title).join(", ")
      }

      const staticProgress = Math.floor(Math.random() * 100)
      const userStatus = user.courses_enrolled && !user.courses_enrolled.includes("None") ? "Inactive" : "Active";


      return {
        "Sr No": index + 1,
        ID: user.user_id,
        Occupation: user.occupation || "N/A",
        "First Name": user.full_name ? user.full_name.split(" ")[0] : "N/A",
        "Middle Name": user.full_name ? user.full_name.split(" ").slice(1, -1).join(" ") || "N/A" : "N/A",
        "Last Name": user.full_name ? user.full_name.split(" ").slice(-1)[0] : "N/A",
        "Mobile Number": user.mobile_number || "N/A",
        Email: user.email || "N/A",
        Address: user.address_line1 || "N/A",
        Village: user.village || "N/A",
        Tehsil: user.tehsil || "N/A",
        District: user.district || "N/A",
        State: user.state || "N/A",
        Country: user.country || "N/A",
        "Pin code": user.pin_code || "N/A",
        Education: user.education || "N/A",
        Degree: user.degree || "N/A",
        "Course (Enrolled)": courseNames,
        "Course Enrolled Date": formattedJoinDate,
        "Course (Cancelled)": safe(user.course_cancelled),
        "Course(Expired)": safe(user.course_expired),
        "Membership (Enrolled)": safe(user.membership_enrolled),
        "Membership Enrolled Date": safe(user.membership_enrolled_date),
        "Membership (Cancelled)": safe(user.membership_cancelled),
        "Membership (Expired)": safe(user.membership_expired),
        "Registration Date": registrationDate,
        "Account Created Date": accountCreatedDate,
        Status: userStatus,
        "Last seen": user.last_seen || "N/A",
        Progress: user.overall_progress + "%",
        "Quiz Progress": user.quiz_progress + "%",
        Achivement: "N/A",
        Certificate: user.overall_progress >= 99 ? "Yes" : user.overall_progress >= 50 ? "In Progress" : "No",
        Agriculture: user.farmer_farm_type ? "Yes" : "No",
        Livestock: user.farmer_agriculture_type ? "Yes" : "No",
        Poultry: user.farmer_poultry_type ? "Yes" : "No",
        Aquaculture: user.farmer_aquaculture_type ? "Yes" : "No",
        Doctor: user.doctor_specialization ? "Yes" : "No",
        "Nature of business": user.business_nature || "N/A",
        "Company Name": user.business_company_name || "N/A",
        "Company Address": user.business_company_address || "N/A",
        Designation: user.employee_designation || user.service_designation || "N/A",
        Dairy: user.farmer_livestock_type === "Dairy" ? "Yes" : "No",
        Swine: "No",
        "Employee IDN": user.employee_id || "N/A",
        Department: user.employee_department || "N/A",
        Division: user.employee_division || "N/A",
        Headquarter: user.employee_headquarter || "N/A",
        SubDivision: user.employee_subdivision || "N/A",
        rawUserData: user, // Keep original data for modal
      }
    })
  }

  const transformedData = transformData(apiData)

  // All available fields
  const allFields = [
    "Sr No",
    "ID",
    "Occupation",
    "First Name",
    "Middle Name",
    "Last Name",
    "Mobile Number",
    "Email",
    "Address",
    "Village",
    "Tehsil",
    "District",
    "State",
    "Country",
    "Pin code",
    "Education",
    "Degree",
    "Course (Enrolled)",
    "Course Enrolled Date",
    "Course (Cancelled)",
    "Course(Expired)",
    "Membership (Enrolled)",
    "Membership Enrolled Date",
    "Membership (Cancelled)",
    "Membership (Expired)",
    "Registration Date",
    "Account Created Date",
    "Status",
    "Last seen",
    "Progress",
    "Quiz Progress",
    "Achivement",
    "Certificate",
    "Agriculture",
    "Livestock",
    "Poultry",
    "Aquaculture",
    "Doctor",
    "Nature of business",
    "Company Name",
    "Company Address",
    "Designation",
    "Dairy",
    "Swine",
    "Employee IDN",
    "Department",
    "Division",
    "Headquarter",
    "SubDivision",
  ]

  // Company Employee specific fields
  const companyEmployeeFields = [
    "Sr No",
    "ID",
    "First Name",
    "Middle Name",
    "Last Name",
    "Mobile Number",
    "Email",
    "Employee IDN",
    "Department",
    "Designation",
    "Division",
    "Headquarter",
    "SubDivision",
    "Education",
    "Degree",
    "Registration Date",
    "Account Created Date",
    "Status",
    "Course (Enrolled)",
    "Course Enrolled Date",
    "Membership (Enrolled)",
    "Membership Enrolled Date",
    "Membership (Expired)",
    "Progress",
    "Quiz Progress",
    "Achivement",
    "Certificate",
  ]

  // Other occupation fields
  const otherOccupationFields = [
    "Sr No",
    "ID",
    "First Name",
    "Middle Name",
    "Last Name",
    "Mobile Number",
    "Email",
    "Education",
    "Degree",
    "Course (Enrolled)",
    "Course Enrolled Date",
    "Course(Expired)",
    "Membership (Enrolled)",
    "Membership Enrolled Date",
    "Membership (Expired)",
    "Registration Date",
    "Account Created Date",
    "Status",
    "Progress",
    "Quiz Progress",
    "Achivement",
    "Certificate",
    "Address",
    "Village",
    "Tehsil",
    "District",
    "State",
    "Country",
    "Pin code",
  ]

  // Course Progress specific fields
  const courseProgressFields = [
    "Sr No",
    "ID",
    "Occupation",
    "First Name",
    "Last Name",
    "Mobile Number",
    "Email",
    "Course (Enrolled)",
    "Course Enrolled Date",
    "Course(Expired)",
    "Membership (Enrolled)",
    "Membership Enrolled Date",
    "Membership (Expired)",
    "Progress",
    "Quiz Progress",
    "Certificate",
    "Status",
  ]

  // LMS Account Status specific fields
  const accountStatusFields = [
    "Sr No",
    "ID",
    "Occupation",
    "First Name",
    "Middle Name",
    "Last Name",
    "Mobile Number",
    "Email",
    "Address",
    "Village",
    "Tehsil",
    "District",
    "State",
    "Country",
    "Pin code",
    "Education",
    "Degree",
    "Account Created Date",
    "Status",
  ]

  // Course status options
  const courseStatusOptions = ["Active", "Cancelled", "Expired"]

  // Certificate status options
  const certificateStatusOptions = ["Yes", "In Progress", "No"]

  // Determine which fields to show based on active section and filters
  const getFieldsToShow = () => {
    const occ = (filters.occupation || "").toLowerCase();

    if (activeSection === "course-progress") {
      return courseProgressFields;
    } else if (activeSection === "account-status") {
      return accountStatusFields;
    } else if (activeSection === "reports") {
      if (occ === "company employee") {
        return companyEmployeeFields;
      } else if (filters.occupation && occ !== "company employee") {
        return otherOccupationFields;
      }
      return allFields;
    }
    return allFields;
  };


  const fieldsToShow = getFieldsToShow()

  // Filter data based on all conditions
  const filteredData = transformedData.filter((item) => {
    const occ = (item["Occupation"] || "").toLowerCase();
    const filterOcc = (filters.occupation || "").toLowerCase();

    const div = (item["Division"] || "").toLowerCase();
    const filterDiv = (filters.division || "").toLowerCase();

    const subDiv = (item["SubDivision"] || "").toLowerCase();
    const filterSubDiv = (filters.subDivision || "").toLowerCase();

    const desig = (item["Designation"] || "").toLowerCase();
    const filterDesig = (filters.designation || "").toLowerCase();

    const matchesSearch =
      !searchTerm ||
      Object.values(item).some((value) =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesOccupation =
      !filters.occupation || occ === filterOcc;

    const matchesDivision =
      !filters.division ||
      (filterOcc === "company employee" && div === filterDiv);

    const matchesSubDivision =
      !filters.subDivision ||
      (filterOcc === "company employee" && subDiv === filterSubDiv);

    const matchesDesignation =
      !filters.designation ||
      (filterOcc === "company employee" && desig === filterDesig);

    const matchesCourseStatus =
      !filters.courseStatus ||
      (filters.courseStatus === "Active" &&
        !item["Course (Cancelled)"] &&
        !item["Course(Expired)"]) ||
      (filters.courseStatus === "Cancelled" && item["Course (Cancelled)"]) ||
      (filters.courseStatus === "Expired" && item["Course(Expired)"]);

    return (
      matchesSearch &&
      matchesOccupation &&
      matchesDivision &&
      matchesSubDivision &&
      matchesDesignation &&
      matchesCourseStatus
    );
  });


  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage
  const indexOfFirstRow = indexOfLastRow - rowsPerPage
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow)
  const totalPages = Math.ceil(filteredData.length / rowsPerPage)

  const paginate = (pageNumber) => setCurrentPage(pageNumber)

  // Update filter handler
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }))

    // Reset dependent filters when occupation changes
    if (filterName === "occupation") {
      setFilters((prev) => ({
        ...prev,
        division: "",
        subDivision: "",
        designation: "",
      }))
    }

    // Reset to first page when filters change
    setCurrentPage(1)
  }

  // Handle date range change
  const handleDateRangeChange = (range) => {
    setDateRange(range)
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A"
    const options = { year: "numeric", month: "short", day: "numeric" }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  // Statistics
  const stats = {
    total: transformedData.length,
    certified: transformedData.filter((user) => user["Certificate"] === "Yes").length,
    inProgress: transformedData.filter((user) => user["Certificate"] === "In Progress").length,
    avgProgress: Math.round(
      transformedData.reduce((sum, user) => {
        const progress = Number.parseFloat(user["Progress"]) || 0
        return sum + progress
      }, 0) / (transformedData.length || 1), // Avoid division by zero
    ),
    activeCourses: transformedData.filter((user) => !user["Course (Cancelled)"] && !user["Course(Expired)"]).length,
    activeMemberships: transformedData.filter(
      (user) => !user["Membership (Cancelled)"] && !user["Membership (Expired)"],
    ).length,
  }

  // Section navigation
  const sections = [
    { id: "reports", label: "Reports", icon: FileText },
    { id: "course-progress", label: "Course Progress Report", icon: TrendingUp },
    { id: "account-status", label: "LMS Account Status", icon: Users },
  ]

  // Export to CSV
  const exportToCSV = () => {
    const dataToExport = filteredData.map((item) => {
      const row = {}
      fieldsToShow.forEach((field) => {
        row[field] = item[field] || "N/A"
      })
      return row
    })

    const csvHeader = fieldsToShow.join(",") + "\n"
    const csvRows = dataToExport.map((item) => fieldsToShow.map((field) => `"${item[field]}"`).join(",")).join("\n")

    const csvContent = csvHeader + csvRows
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.setAttribute("href", url)
    link.setAttribute("download", `${activeSection}_report_${new Date().toISOString().slice(0, 10)}.csv`)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Export to Excel
  const exportToExcel = () => {
    const dataToExport = filteredData.map((item) => {
      const row = {}
      fieldsToShow.forEach((field) => {
        row[field] = item[field] || "N/A"
      })
      return row
    })

    const worksheet = XLSX.utils.json_to_sheet(dataToExport)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report")
    XLSX.writeFile(workbook, `${activeSection}_report_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  // Handle export button click
  const handleExportClick = () => {
    setShowExportOptions(!showExportOptions)
  }

  if (loading) {
    return <AttractiveLoader />
  }

  if (error) {
    return (
      <div
        style={{
          padding: "30px",
          textAlign: "center",
          color: "#ef4444",
          backgroundColor: "#fef2f2",
          borderRadius: "12px",
          margin: "20px",
          border: "1px solid #fecaca",
        }}
      >
        <XCircle style={{ width: "48px", height: "48px", margin: "0 auto 16px", color: "#ef4444" }} />
        <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "8px" }}>Error Loading Data</h3>
        <p style={{ marginBottom: "16px" }}>{error}</p>
        {error.includes("Authentication") && (
          <div
            style={{
              backgroundColor: "#fef3c7",
              border: "1px solid #fbbf24",
              borderRadius: "8px",
              padding: "16px",
              marginTop: "16px",
              textAlign: "left",
            }}
          >
            <h4 style={{ color: "#92400e", fontSize: "14px", fontWeight: "600", marginBottom: "8px" }}>
              Authentication Help:
            </h4>
            <ul style={{ color: "#92400e", fontSize: "14px", margin: 0, paddingLeft: "20px" }}>
              <li>Make sure you're logged in to the system</li>
              <li>Check if your session has expired and try logging in again</li>
              <li>Verify that your JWT token is valid</li>
              <li>Contact your administrator if the problem persists</li>
            </ul>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ padding: "30px", backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header Section */}
      <div style={{ marginBottom: "40px" }}>
        <h1 style={{ fontSize: "32px", fontWeight: "bold", color: "#1e293b", marginBottom: "8px" }}>
          {activeSection === "course-progress"
            ? "Course Progress Report Dashboard"
            : activeSection === "account-status"
              ? "LMS Account Status Dashboard"
              : "Reports & Analytics Dashboard"}
        </h1>
        <p style={{ color: "#64748b", fontSize: "16px" }}>
          {activeSection === "course-progress"
            ? "Comprehensive course progress and learning analytics"
            : activeSection === "account-status"
              ? "Account management and status tracking system"
              : "Comprehensive Learning Management System Overview"}
        </p>
      </div>

      {/* Section Navigation */}
      <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: "30px" }}>
        {sections.map((section) => {
          const Icon = section.icon
          return (
            <button
              key={section.id}
              onClick={() => {
                setActiveSection(section.id)
                setActiveTab("overview")
              }}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "15px 25px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderBottom: activeSection === section.id ? "3px solid #3b82f6" : "3px solid transparent",
                backgroundColor: activeSection === section.id ? "#f8fafc" : "white",
                color: activeSection === section.id ? "#3b82f6" : "#64748b",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <Icon style={{ width: "18px", height: "18px", marginRight: "8px" }} />
              {section.label}
            </button>
          )
        })}
      </div>

      {/* Statistics Cards - Show for course progress and account status */}
      {(activeSection === "course-progress" || activeSection === "account-status") && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px",
            marginBottom: "30px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "25px",
              borderRadius: "12px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
              border: "1px solid #f1f5f9",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "5px" }}>
                  Total {activeSection === "account-status" ? "Accounts" : "Students"}
                </p>
                <p style={{ fontSize: "28px", fontWeight: "bold", color: "#3b82f6" }}>{stats.total}</p>
              </div>
              <Users style={{ width: "32px", height: "32px", color: "#3b82f6" }} />
            </div>
          </div>

          {activeSection === "account-status" ? (
            <>
              <div
                style={{
                  backgroundColor: "white",
                  padding: "25px",
                  borderRadius: "12px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "5px" }}>Active Accounts</p>
                    <p style={{ fontSize: "28px", fontWeight: "bold", color: "#10b981" }}>
                      {transformedData.filter((user) => user["Status"] === "Active").length}
                    </p>
                  </div>
                  <CheckCircle style={{ width: "32px", height: "32px", color: "#10b981" }} />
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "white",
                  padding: "25px",
                  borderRadius: "12px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "5px" }}>Inactive Accounts</p>
                    <p style={{ fontSize: "28px", fontWeight: "bold", color: "#f59e0b" }}>
                      {transformedData.filter((user) => user["Status"] === "Inactive").length}
                    </p>
                  </div>
                  <Clock style={{ width: "32px", height: "32px", color: "#f59e0b" }} />
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "white",
                  padding: "25px",
                  borderRadius: "12px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "5px" }}>Suspended</p>
                    <p style={{ fontSize: "28px", fontWeight: "bold", color: "#ef4444" }}>
                      {transformedData.filter((user) => user["Status"] === "Suspended").length}
                    </p>
                  </div>
                  <XCircle style={{ width: "32px", height: "32px", color: "#ef4444" }} />
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  backgroundColor: "white",
                  padding: "25px",
                  borderRadius: "12px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "5px" }}>Certified</p>
                    <p style={{ fontSize: "28px", fontWeight: "bold", color: "#10b981" }}>{stats.certified}</p>
                  </div>
                  <Award style={{ width: "32px", height: "32px", color: "#10b981" }} />
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "white",
                  padding: "25px",
                  borderRadius: "12px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "5px" }}>In Progress</p>
                    <p style={{ fontSize: "28px", fontWeight: "bold", color: "#f59e0b" }}>{stats.inProgress}</p>
                  </div>
                  <Clock style={{ width: "32px", height: "32px", color: "#f59e0b" }} />
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "white",
                  padding: "25px",
                  borderRadius: "12px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "5px" }}>Avg Progress</p>
                    <p style={{ fontSize: "28px", fontWeight: "bold", color: "#8b5cf6" }}>{stats.avgProgress}%</p>
                  </div>
                  <BarChart3 style={{ width: "32px", height: "32px", color: "#8b5cf6" }} />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Date Range and Export Controls */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "40px",
          padding: "20px",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Calendar style={{ color: "#7366ff", width: "20px", height: "20px" }} />
          <div style={{ display: "flex", border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
            {["week", "month", "quarter", "year"].map((range) => (
              <button
                key={range}
                onClick={() => handleDateRangeChange(range)}
                style={{
                  padding: "10px 20px",
                  background: dateRange === range ? "linear-gradient(135deg, #ff5baf, #7366ff)" : "white",
                  color: dateRange === range ? "white" : "#64748b",
                  border: "none",
                  cursor: "pointer",
                  textTransform: "capitalize",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {range}
              </button>
            ))}
          </div>
        </div>

        <div style={{ position: "relative" }}>
          <button
            onClick={handleExportClick}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 24px",
              background: "linear-gradient(135deg, #ff5baf, #7366ff)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            <Download style={{ width: "16px", height: "16px" }} />
            Export{" "}
            {activeSection === "course-progress"
              ? "Progress Data"
              : activeSection === "account-status"
                ? "Account Data"
                : "Reports"}
          </button>

          {showExportOptions && (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: "100%",
                marginTop: "8px",
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                zIndex: 10,
                minWidth: "180px",
                overflow: "hidden",
              }}
            >
              <button
                onClick={() => {
                  exportToCSV()
                  setShowExportOptions(false)
                }}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  textAlign: "left",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#334155",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f5f9")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
              >
                <FileText style={{ width: "16px", height: "16px" }} />
                Export as CSV
              </button>
              <button
                onClick={() => {
                  exportToExcel()
                  setShowExportOptions(false)
                }}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  textAlign: "left",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#334155",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f5f9")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
              >
                <Download style={{ width: "16px", height: "16px" }} />
                Export as Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Search and Filters Section */}
      <div
        style={{
          backgroundColor: "white",
          padding: "30px",
          borderRadius: "16px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
          border: "1px solid #f1f5f9",
          marginBottom: "40px",
        }}
      >
        {/* Search Bar */}
        <div style={{ position: "relative", marginBottom: "25px" }}>
          <Search
            style={{
              position: "absolute",
              left: "15px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              width: "18px",
              height: "18px",
            }}
          />
          <input
            type="text"
            placeholder="Search across all fields..."
            style={{
              width: "100%",
              paddingLeft: "50px",
              paddingRight: "20px",
              paddingTop: "15px",
              paddingBottom: "15px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "16px",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
          />
        </div>

        {/* Filter Controls */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "15px",
          }}
        >
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: "flex",
              alignItems: "center",
              color: "#64748b",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              padding: "8px",
            }}
          >
            <Filter style={{ width: "16px", height: "16px", marginRight: "8px" }} />
            Advanced Filters
            <ChevronDown
              style={{
                width: "16px",
                height: "16px",
                marginLeft: "4px",
                transform: showFilters ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </button>

          <div style={{ fontSize: "14px", color: "#64748b" }}>Showing {filteredData.length} records</div>
        </div>

        {/* Expandable Filters */}
        {showFilters && (
          <div style={{ paddingTop: "25px", borderTop: "1px solid #e2e8f0", marginTop: "20px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "20px",
                maxWidth: "1000px",
              }}
            >
              {/* Occupation Filter */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  Occupation
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  value={filters.occupation}
                  onChange={(e) => handleFilterChange("occupation", e.target.value)}
                >
                  <option value="">Select Occupation</option>
                  {occupationOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Division Filter - Only show if occupation is Company Employee */}
              {filters.occupation === "Company employee" && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Division
                  </label>
                  <select
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                    value={filters.division}
                    onChange={(e) => handleFilterChange("division", e.target.value)}
                  >
                    <option value="">Select Division</option>
                    {divisionOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Sub-Division Filter - Only show if occupation is Company Employee */}
              {filters.occupation === "Company employee" && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Sub-Division
                  </label>
                  <select
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                    value={filters.subDivision}
                    onChange={(e) => handleFilterChange("subDivision", e.target.value)}
                  >
                    <option value="">Select Sub-Division</option>
                    {subDivisionOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Designation Filter - Only show if occupation is Company Employee */}
              {filters.occupation === "Company employee" && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Designation
                  </label>
                  <select
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                    value={filters.designation}
                    onChange={(e) => handleFilterChange("designation", e.target.value)}
                  >
                    <option value="">Select Designation</option>
                    {designationOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Course Status Filter */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  Course Status
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  value={filters.courseStatus}
                  onChange={(e) => handleFilterChange("courseStatus", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {courseStatusOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Certificate Status Filter */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  Certificate Status
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  value={filters.certificateStatus}
                  onChange={(e) => handleFilterChange("certificateStatus", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {certificateStatusOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table Section */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
          border: "1px solid #f1f5f9",
          overflow: "hidden",
        }}
      >
        {/* Table Content */}
        <div style={{ padding: "30px" }}>
          {/* Info Banner */}
          <div
            style={{
              backgroundColor: activeSection === "course-progress" ? "#e0f2fe" : "#f0f9ff",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Info style={{ width: "16px", height: "16px", color: "#0369a1" }} />
            <span style={{ color: "#0369a1", fontSize: "14px" }}>
              {activeSection === "course-progress"
                ? "Showing Course Progress Report with learning analytics and completion tracking"
                : activeSection === "account-status"
                  ? "Showing LMS Account Status with user management and account details"
                  : filters.occupation === "Company employee"
                    ? "Showing Company employee view with all relevant business fields"
                    : filters.occupation && filters.occupation !== "Company employee"
                      ? `Showing ${filters.occupation} view with relevant fields and location information`
                      : "Showing complete reports with all available fields"}
            </span>
          </div>

          <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#1e293b", marginBottom: "25px" }}>
            {activeSection === "course-progress"
              ? "Course Progress & Learning Analytics"
              : activeSection === "account-status"
                ? "LMS Account Status & Management"
                : filters.occupation
                  ? `${filters.occupation} Details`
                  : "Complete Reports Overview"}
          </h3>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1200px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc" }}>
                  {fieldsToShow.map((field) => (
                    <th
                      key={field}
                      style={{
                        padding: "15px 12px",
                        textAlign: "left",
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderBottom: "1px solid #e2e8f0",
                        minWidth: "120px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {field}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row, index) => (
                  <tr
                    key={index}
                    style={{ transition: "background-color 0.1s", cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
                  >
                    {fieldsToShow.map((field) => (
                      <td
                        key={field}
                        style={{
                          padding: "16px 12px",
                          fontSize: "13px",
                          color: "#374151",
                          borderBottom: "1px solid #f1f5f9",
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(() => {
                          switch (field) {
                            case "Progress":
                              return (
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: "100px" }}>
                                  <div
                                    style={{
                                      width: "50px",
                                      height: "6px",
                                      backgroundColor: "#e2e8f0",
                                      borderRadius: "3px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: row[field],
                                        height: "100%",
                                        backgroundColor: "#3b82f6",
                                        borderRadius: "3px",
                                      }}
                                    ></div>
                                  </div>
                                  <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
                                    {row[field]}
                                  </span>
                                </div>
                              )

                            case "Status":
                              return (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    padding: "4px 12px",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    borderRadius: "20px",
                                    backgroundColor:
                                      row[field] === "Active"
                                        ? "#dcfce7"
                                        : row[field] === "Inactive"
                                          ? "#fef3c7"
                                          : "#fee2e2",
                                    color:
                                      row[field] === "Active"
                                        ? "#166534"
                                        : row[field] === "Inactive"
                                          ? "#92400e"
                                          : "#b91c1c",
                                  }}
                                >
                                  {row[field] || "N/A"}
                                </span>
                              )

                            case "Certificate":
                              return (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    padding: "3px 8px",
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    borderRadius: "12px",
                                    backgroundColor:
                                      row[field] === "Yes"
                                        ? "#dcfce7"
                                        : row[field] === "In Progress"
                                          ? "#fef3c7"
                                          : "#f1f5f9",
                                    color:
                                      row[field] === "Yes"
                                        ? "#166534"
                                        : row[field] === "In Progress"
                                          ? "#92400e"
                                          : "#64748b",
                                  }}
                                >
                                  {row[field] || "N/A"}
                                </span>
                              )

                            case "Quiz Progress":
                              return (
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: "100px" }}>
                                  <div
                                    style={{
                                      width: "50px",
                                      height: "6px",
                                      backgroundColor: "#e2e8f0",
                                      borderRadius: "3px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: row[field],
                                        height: "100%",
                                        backgroundColor: "#3b82f6",
                                        borderRadius: "3px",
                                      }}
                                    ></div>
                                  </div>
                                  <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
                                    {row[field]}
                                  </span>
                                </div>
                              )

                            case "Course (Enrolled)":
                              return (
                                <div
                                  style={{
                                    color: "#3b82f6",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  onClick={() => {
                                    setSelectedUser(row)
                                    setShowCourseModal(true)
                                  }}
                                >
                                  {row[field] || "N/A"}
                                </div>
                              )

                            case "Account Created Date":
                            case "Registration Date":
                              return (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <Clock style={{ width: "12px", height: "12px", color: "#64748b" }} />
                                  <span style={{ fontSize: "12px" }}>{formatDate(row[field])}</span>
                                </div>
                              )

                            case "Course (Cancelled)":
                            case "Course(Expired)":
                            case "Membership (Cancelled)":
                            case "Membership (Expired)":
                              return (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  {row[field] && row[field] !== "N/A" ? (
                                    <>
                                      <XCircle style={{ width: "12px", height: "12px", color: "#ef4444" }} />
                                      <span style={{ fontSize: "12px" }}>{formatDate(row[field])}</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle style={{ width: "12px", height: "12px", color: "#10b981" }} />
                                      <span style={{ fontSize: "12px" }}>N/A</span>
                                    </>
                                  )}
                                </div>
                              )

                            case "Last seen":
                              return (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <div
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "50%",
                                      backgroundColor: row[field] === "Online" ? "#10b981" : "#64748b",
                                    }}
                                  ></div>
                                  <span style={{ fontSize: "12px" }}>{row[field] || "Offline"}</span>
                                </div>
                              )

                            default:
                              if (
                                [
                                  "Agriculture",
                                  "Livestock",
                                  "Poultry",
                                  "Aquaculture",
                                  "Dairy",
                                  "Swine",
                                  "Doctor",
                                ].includes(field)
                              ) {
                                return (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      padding: "3px 8px",
                                      fontSize: "11px",
                                      fontWeight: "500",
                                      borderRadius: "12px",
                                      backgroundColor: row[field] === "Yes" ? "#dcfce7" : "#fee2e2",
                                      color: row[field] === "Yes" ? "#166534" : "#b91c1c",
                                    }}
                                  >
                                    {row[field] || "No"}
                                  </span>
                                )
                              }
                              return (
                                <span title={row[field]} style={{ fontSize: "12px" }}>
                                  {row[field] || "N/A"}
                                </span>
                              )
                          }
                        })()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "20px",
              paddingTop: "20px",
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "14px", color: "#64748b" }}>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value))
                  setCurrentPage(1) // Reset to first page when rows per page changes
                }}
                style={{
                  padding: "6px 10px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "4px",
                  fontSize: "14px",
                  outline: "none",
                }}
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "14px", color: "#64748b" }}>
                Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredData.length)} of{" "}
                {filteredData.length} entries
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <button
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 10px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "4px",
                  backgroundColor: currentPage === 1 ? "#f1f5f9" : "white",
                  color: currentPage === 1 ? "#94a3b8" : "#334155",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <ChevronLeft style={{ width: "16px", height: "16px" }} />
                First
              </button>

              <button
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 10px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "4px",
                  backgroundColor: currentPage === 1 ? "#f1f5f9" : "white",
                  color: currentPage === 1 ? "#94a3b8" : "#334155",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "4px",
                      backgroundColor: currentPage === pageNum ? "#3b82f6" : "white",
                      color: currentPage === pageNum ? "white" : "#334155",
                      cursor: "pointer",
                      fontWeight: currentPage === pageNum ? "600" : "normal",
                    }}
                  >
                    {pageNum}
                  </button>
                )
              })}

              <button
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 10px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "4px",
                  backgroundColor: currentPage === totalPages ? "#f1f5f9" : "white",
                  color: currentPage === totalPages ? "#94a3b8" : "#334155",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>

              <button
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 10px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "4px",
                  backgroundColor: currentPage === totalPages ? "#f1f5f9" : "white",
                  color: currentPage === totalPages ? "#94a3b8" : "#334155",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                Last
                <ChevronRight style={{ width: "16px", height: "16px" }} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showCourseModal && <OptimizedCourseModal user={selectedUser} onClose={() => setShowCourseModal(false)} />}
    </div>
  )
}

export default Dashboard